import { z } from "zod";
import { TFunction } from "i18next";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";

// LoginFormSchema -> Zod Schema -> Login Form
export const LoginFormSchema = z.object({
  username: z
    .string()
    .min(3, {
      message: "Username must be at least 3 characters.",
    })
    .max(30, {
      message: "Username must be at most 30 characters.",
    })
    .regex(/^[a-z0-9._-]+$/, {
      message:
        " Username may only consist of lowercase letters, numbers, periods, underscores, and hyphens.",
    }),
  password: z
    .string()
    .min(6, {
      message: "Password must be at least 6 characters.",
    })
    .max(MAX_PASSWORD_LENGTH, {
      message: `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`,
    }),
});

// LoginFormType -> Infer the type from the Zod Schema -> Login Form
export type LoginFormType = z.infer<typeof LoginFormSchema>;

// EmailVerificationSchema -> used by Screen A (email entry step)
export const EmailVerificationSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address." }),
});

export type EmailVerificationType = z.infer<typeof EmailVerificationSchema>;

// VerificationCodeSchema -> used by Screen B (code entry step)
export const VerificationCodeSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, { message: "Code must be exactly 6 digits." }),
});

export type VerificationCodeType = z.infer<typeof VerificationCodeSchema>;

// getForgotUsernameSchema -> Zod schema factory for the forgot-username form.
// Takes the i18next `t` so the client-side validation message is localized
// (matches getUsernameFormSchema in forgotPassword.ts). Client-side email
// validation is the primary guard, so a server 400 is rare.
export const getForgotUsernameSchema = (tf: TFunction) =>
  z.object({
    email: z.string().email({ message: tf("forgotUsername.invalidEmail") }),
  });

// ForgotUsernameType -> Infer the type from the Zod schema -> Forgot-Username
export type ForgotUsernameType = z.infer<
  ReturnType<typeof getForgotUsernameSchema>
>;

// RegisterFormBaseSchema -> plain ZodObject used for .omit() / .intersection()
// email is collected in Screen A (EmailVerificationStep) and passed as verifiedEmail — not part of this form
const RegisterFormBaseSchema = z.object({
  username: z
    .string()
    .min(4, {
      message: "Username must be at least 4 characters.",
    })
    .max(30, {
      message: "Username must be at most 30 characters.",
    })
    .regex(/^[a-z0-9._-]+$/, {
      message:
        " Username may only consist of lowercase letters, numbers, periods, underscores, and hyphens.",
    }),
  password: z
    .string()
    .min(6, {
      message: "Password must be at least 6 characters.",
    })
    .max(MAX_PASSWORD_LENGTH, {
      message: `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`,
    }),
  confirmPassword: z
    .string()
    .min(6, {
      message: "Password must be at least 6 characters.",
    })
    .max(MAX_PASSWORD_LENGTH, {
      message: `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`,
    }),
  acceptedTc: z.boolean().refine((val) => val === true, {
    message: "You must accept the Terms & Conditions to register.",
  }),
});

// RegisterFormSchema -> adds cross-field refinements on top of the base
export const RegisterFormSchema = RegisterFormBaseSchema.refine(
  (data) => data.password === data.confirmPassword,
  {
    message: "Passwords must match.",
    path: ["confirmPassword"],
  },
);

// RegisterFormType -> Infer the type from the Zod Schema -> Register Form
export type RegisterFormType = z.infer<typeof RegisterFormSchema>;

// RegisterKeySchema -> Zod Schema -> Register Key
const RegisterKeySchema = z.object({
  public_keys: z.string().min(1, {
    message: "Public keys must be at least 1 character long.",
  }),
  private_keys: z.string().min(2, {
    message: "Private keys must be at least 1 character long.",
  }),
});

// RegisterFormWithKeys -> base schema (FE-only acceptedTc omitted) intersected with key fields
export const RegisterFormWithKeys = z.intersection(
  RegisterFormBaseSchema.omit({ acceptedTc: true }),
  RegisterKeySchema,
);

// RegisterFormTypeWithKeys -> Infer the type from the Zod Schema -> Register Form
export type RegisterFormTypeWithKeys = z.infer<typeof RegisterFormWithKeys>;
